<template>
  <v-dialog
    v-model="dialog"
    max-width="700"
    persistent
    @click:outside="closeDialog"
    @keydown.esc="closeDialog"
    content-class="quick-search-dialog"
  >
    <v-card class="quick-search-card">
      <!-- Botón de cerrar en la esquina -->
      <v-btn
        icon
        small
        @click="closeDialog"
        class="close-modal-btn"
        absolute
        top
        right
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <!-- Buscador -->
      <v-card-text class="pa-0">
        <v-text-field
          ref="searchInput"
          v-model="search"
          placeholder="Buscar en el menú..."
          prepend-inner-icon="mdi-magnify"
          solo
          flat
          hide-details
          autofocus
          class="search-input"
          @input="searchMenuOptions"
          @keydown.native="validarAlfanumerico"
          @paste="onPasteAlphaNumeric"
          @keydown.down.prevent="navigateDown"
          @keydown.up.prevent="navigateUp"
          @keydown.enter.prevent="selectHighlighted"
        >
        </v-text-field>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Resultados -->
      <v-card-text class="pa-0 results-container">
        <v-list v-if="menuLocal.length > 0" class="pa-0">
          <div v-for="(item, i) in flattenedMenu" :key="`item-${i}`">
            <v-list-item
              @click="selectItem(item)"
              :class="{ 'highlighted-item': i === highlightedIndex }"
              class="result-item"
            >
              <v-list-item-icon class="mr-3">
                <v-icon color="#313945" size="20">{{ item.icono }}</v-icon>
              </v-list-item-icon>

              <v-list-item-content>
                <v-list-item-title class="result-title">
                  {{ item.titulo }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="item.breadcrumb" class="caption">
                  {{ item.breadcrumb }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action>
                <v-icon size="16" color="grey lighten-1">mdi-arrow-right</v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="i < flattenedMenu.length - 1"></v-divider>
          </div>
        </v-list>

        <!-- Sin resultados -->
        <div v-else class="no-results pa-8 text-center">
          <v-icon size="48" color="grey lighten-1">mdi-magnify</v-icon>
          <p class="mt-4 grey--text">No se encontraron resultados</p>
        </div>
      </v-card-text>

      <!-- Footer con ayuda -->
      <v-divider></v-divider>
      <v-card-actions class="pa-3 grey lighten-4">
        <v-row dense align="center" class="text-caption grey--text text--darken-1">
          <v-col cols="auto">
            <v-chip x-small outlined class="mr-1">
              <v-icon x-small left>mdi-arrow-up</v-icon>
              <v-icon x-small>mdi-arrow-down</v-icon>
            </v-chip>
            <span>navegar</span>
          </v-col>
          <v-col cols="auto">
            <v-chip x-small outlined class="mr-1">
              <v-icon x-small>mdi-keyboard-return</v-icon>
            </v-chip>
            <span>seleccionar</span>
          </v-col>
          <v-col cols="auto">
            <v-chip x-small outlined class="mr-1">
              <span>ESC</span>
            </v-chip>
            <span>cerrar</span>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'AppQuickSearchModal',
  props: {
    menu: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      dialog: false,
      search: '',
      menuLocal: [],
      highlightedIndex: 0,
    };
  },
  computed: {
    flattenedMenu() {
      const flattened = [];
      
      const flatten = (items, breadcrumb = '') => {
        items.forEach(item => {
          // Agregar el item actual
          const itemWithBreadcrumb = {
            ...item,
            breadcrumb: breadcrumb,
          };
          
          // Si no tiene dependencias o es una hoja final, agregar
          if (!item.dependencias || item.dependencias.length === 0) {
            flattened.push(itemWithBreadcrumb);
          }
          
          // Si tiene dependencias, procesar recursivamente
          if (item.dependencias && item.dependencias.length > 0) {
            const newBreadcrumb = breadcrumb 
              ? `${breadcrumb} > ${item.titulo}` 
              : item.titulo;
            flatten(item.dependencias, newBreadcrumb);
          }
        });
      };
      
      flatten(this.menuLocal);
      return flattened;
    },
  },
  watch: {
    menu: {
      handler(val) {
        this.menuLocal = val;
      },
      immediate: true,
    },
    dialog(val) {
      if (val) {
        // Resetear búsqueda al abrir
        this.search = '';
        this.highlightedIndex = 0;
        this.menuLocal = this.menu;
        
        
        // Focus en el input
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    search() {
      // Resetear el índice cuando cambia la búsqueda
      this.highlightedIndex = 0;
    },
  },
  mounted() {
    // Escuchar Ctrl+K o Cmd+K globalmente
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleGlobalKeydown);
  },
  methods: {
    handleGlobalKeydown(e) {
      // Ctrl+K (Windows/Linux) o Cmd+K (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        this.openDialog();
      }
    },
    openDialog() {
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.search = '';
      this.highlightedIndex = 0;
    },
    validarAlfanumerico(e) {
      const key = e.key;

      // Permitir navegación con flechas y enter
      if (['ArrowDown', 'ArrowUp', 'Enter'].includes(key)) {
        return;
      }

      // Dejar pasar combinaciones Ctrl/Cmd/Alt
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Permitir teclas de edición/navegación
      const permitidas = [
        'Backspace',
        'Delete',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Home',
        'End',
      ];
      if (permitidas.includes(key)) return;

      // Bloquear emojis
      const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/u;
      if (emojiRegex.test(key)) {
        e.preventDefault();
        return;
      }

      // Solo letras (incluyendo tildes y ñ), números y espacio
      const alfanumRegex = /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ0-9 ]$/;
      if (!alfanumRegex.test(key)) {
        e.preventDefault();
      }
    },
    onPasteAlphaNumeric(evt) {
      let pasted = evt.clipboardData.getData('text');

      let regex = /^[a-zA-ZáéíóúüñÁÉÍÓÚÜÑ0-9 ]+$/g.test(pasted);
      if (regex == false) {
        evt.preventDefault();
      }

      if (
        pasted.match(/(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu)
      ) {
        evt.preventDefault();
      }
    },
    searchMenuOptions() {
      let menusFound = [];

      if (!this.search) {
        this.menuLocal = this.menu;
        
        return;
      }

      const searchRecursive = (items, base_url = null) => {
        items.forEach(item => {
          const search = this.quitarTildes(this.search)?.toLowerCase();
          const itemSearch = this.quitarTildes(item.titulo)?.toLowerCase();
          if (itemSearch.includes(search)) {
            if (base_url) {
              item.base_url = base_url;
            }
            menusFound.push(item);
          }

          // Continúa la búsqueda en las dependencias del elemento actual
          if (item.dependencias) {
            searchRecursive(item.dependencias, base_url);
          }
        });
      };

      // Inicia búsqueda en menú principal
      searchRecursive(this.menu);
      this.menuLocal = menusFound;
    },
    quitarTildes(str) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    },
    navigateDown() {
      if (this.highlightedIndex < this.flattenedMenu.length - 1) {
        this.highlightedIndex++;
        this.scrollToHighlighted();
      }
    },
    navigateUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
        this.scrollToHighlighted();
      }
    },
    scrollToHighlighted() {
      this.$nextTick(() => {
        const items = document.querySelectorAll('.result-item');
        if (items[this.highlightedIndex]) {
          items[this.highlightedIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }
      });
    },
    selectHighlighted() {
      if (this.flattenedMenu[this.highlightedIndex]) {
        this.selectItem(this.flattenedMenu[this.highlightedIndex]);
      }
    },
    selectItem(item) {
      this.redirect(item?.base_url, `${item?.base_url + item.ruta}`);
      this.closeDialog();
    },
    redirect(base_url, url) {
      const token = localStorage.getItem('token');
      const sessionUuid = localStorage.getItem('session_uuid');

      if (base_url == window.location.origin) {
        window.location = url;
      } else {
        window.location = `${url}?session=${sessionUuid}`;
      }
    },
  },
};
</script>

<style scoped>
.quick-search-dialog {
  overflow: hidden;
  border-radius: 12px !important;
}

.quick-search-card {
  border-radius: 12px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
}

.search-input {
  font-size: 18px;
}

:deep(.search-input .v-input__control) {
  min-height: 70px !important;
}

:deep(.search-input input) {
  font-size: 18px !important;
}

:deep(.search-input .v-input__prepend-inner) {
  margin-top: 0px !important;
}

:deep(.search-input .v-input__append-inner) {
  margin-top: 8px !important;
}

.close-modal-btn {
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.9);
  margin: 4px;
}

.results-container {
  max-height: 400px;
  overflow-y: auto;
}

.result-item {
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 60px !important;
}

.result-item:hover,
.highlighted-item {
  background-color: #f5f5f5 !important;
}

.result-title {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #313945 !important;
}

.no-results {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Scrollbar personalizado */
.results-container::-webkit-scrollbar {
  width: 6px;
}

.results-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.results-container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.results-container::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
